/**
 * The letter canvas mirrors what is being typed into local storage until
 * the server has it, so a crash or a failed autosave does not lose
 * writing. This module owns that mirror for both canvases and for
 * sign-out.
 *
 * Rules (pen test W1, 2026-10-02):
 *  - A mirror belongs to an account. It is stored with the owner's user id
 *    and is only ever read back for that same signed-in account. The
 *    canvas additionally restores it only after the server has returned
 *    the letter to the signed-in person.
 *  - A mirror has a revision. It is removed only when the server has
 *    acknowledged that revision, so a slow save finishing late cannot
 *    remove newer text.
 *  - Sign-out saves each mirror while the session is valid. One that
 *    cannot be saved is never destroyed silently: the person chooses to
 *    keep it on this device (still bound to their account) or discard it.
 *    When the session merely expired there is no moment to ask, so it is
 *    kept.
 *
 * A kept mirror is plain text in this app's local storage until its owner
 * signs back in. Encrypting it would need a key that outlives sign-out,
 * which the web deliberately does not keep.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';

import { createLogger } from '../utils/logger';

const logger = createLogger('letterDraftMirror');

export const LETTER_DRAFT_PREFIX = '@letter_draft_';
const USER_ID_KEY = '@user_id';
const BUDGET_MS = 4000;

interface StoredDraft {
  v: 1;
  owner: string;
  rev: number;
  text: string;
}

export interface LetterDraft {
  text: string;
  rev: number;
}

const keyFor = (letterId: string) => `${LETTER_DRAFT_PREFIX}${letterId}`;

// The newest revision stamped in this run, per key. Updated synchronously
// by stampLetterDraft, so "is this still the newest?" can be answered in
// the same tick as the removal that depends on it.
const latestRev = new Map<string, number>();
let lastRev = 0;
const nextRev = (): number => {
  lastRev = Math.max(lastRev + 1, Date.now());
  return lastRev;
};

async function currentUserId(): Promise<string | null> {
  try {
    const id = await AsyncStorage.getItem(USER_ID_KEY);
    return id && id.trim() ? id : null;
  } catch {
    return null;
  }
}

/** A stored value as a draft; a pre-2026-10-02 mirror is bare text with no owner. */
function parse(raw: string | null): { draft: StoredDraft | null; legacyText: string | null } {
  if (raw === null) return { draft: null, legacyText: null };
  try {
    const value = JSON.parse(raw);
    if (value && value.v === 1 && typeof value.owner === 'string' && typeof value.text === 'string') {
      return { draft: { v: 1, owner: value.owner, rev: Number(value.rev) || 0, text: value.text }, legacyText: null };
    }
  } catch {
    /* not JSON: a legacy mirror */
  }
  return { draft: null, legacyText: raw };
}

/**
 * Records the text being typed. Returns its revision at once; the write
 * happens in the background and is skipped if newer text has arrived or
 * nobody is signed in.
 */
export function stampLetterDraft(letterId: string, text: string): number {
  const key = keyFor(letterId);
  const rev = nextRev();
  latestRev.set(key, rev);
  void (async () => {
    const owner = await currentUserId();
    if (!owner || latestRev.get(key) !== rev) return;
    const stored: StoredDraft = { v: 1, owner, rev, text };
    await AsyncStorage.setItem(key, JSON.stringify(stored));
  })().catch(() => undefined);
  return rev;
}

/**
 * The mirror for this letter, if it belongs to the signed-in account.
 * Call it only after the server has returned the letter to this person: a
 * legacy mirror carries no owner, and that check is what vouches for it.
 */
export async function readLetterDraft(letterId: string): Promise<LetterDraft | null> {
  try {
    const { draft, legacyText } = parse(await AsyncStorage.getItem(keyFor(letterId)));
    if (draft) {
      const owner = await currentUserId();
      return owner && draft.owner === owner ? { text: draft.text, rev: draft.rev } : null;
    }
    return legacyText !== null ? { text: legacyText, rev: 0 } : null;
  } catch {
    return null;
  }
}

/** Removes the mirror once the server has acknowledged revision `rev`, unless newer text exists. */
export async function clearLetterDraftIfUnchanged(letterId: string, rev: number): Promise<void> {
  const key = keyFor(letterId);
  try {
    const newest = latestRev.get(key);
    if (newest !== undefined) {
      // Same tick as the check: a later stamp either changed `newest`
      // already, or writes after this removal.
      if (newest === rev) {
        latestRev.delete(key);
        await AsyncStorage.removeItem(key);
      }
      return;
    }
    // Not typed in this run (restored after a reload): compare what is stored.
    const { draft, legacyText } = parse(await AsyncStorage.getItem(key));
    const storedRev = draft ? draft.rev : legacyText !== null ? 0 : null;
    if (storedRev === rev && latestRev.get(key) === undefined) await AsyncStorage.removeItem(key);
  } catch {
    /* the mirror stays; it is retried on the next save */
  }
}

// One save at a time per letter, in the order the text was written, shared
// by autosave, the Save button and sign-out. Without it an older save
// could reach the server after a newer one and replace the newer text,
// with the local mirror already gone (pen test review, 2026-10-02).
const saveChains = new Map<string, Promise<unknown>>();
const acknowledgedRev = new Map<string, number>();

export interface OrderedSave {
  /** False when newer text had already been saved, or is waiting to be. */
  sent: boolean;
}

export function saveLetterInOrder(
  letterId: string,
  rev: number,
  send: () => Promise<unknown>,
  options: { always?: boolean } = {},
): Promise<OrderedSave> {
  const key = keyFor(letterId);
  const previous = saveChains.get(key) ?? Promise.resolve();
  const run = previous
    .catch(() => undefined)
    .then(async (): Promise<OrderedSave> => {
      // Newer text was acknowledged already, or exists locally and will
      // be saved by its own request: this older text must not be sent.
      // `always` is the Save button: it carries a status change and the
      // text it sends is what is on screen, so it goes regardless (it
      // used to be skipped after an autosave of the same text, leaving
      // the letter a draft while the screen said saved).
      if (!options.always) {
        if ((acknowledgedRev.get(key) ?? -1) > rev) return { sent: false };
        const newest = latestRev.get(key);
        if (newest !== undefined && newest > rev) return { sent: false };
      }
      await send();
      acknowledgedRev.set(key, Math.max(acknowledgedRev.get(key) ?? -1, rev));
      return { sent: true };
    });
  saveChains.set(key, run);
  return run;
}

const timeout = (ms: number) =>
  new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timed out')), Math.max(0, ms)));

const askKeepOrDiscard = (count: number): Promise<'keep' | 'discard'> =>
  new Promise((resolve) => {
    const what = count === 1 ? 'a letter' : `${count} letters`;
    Alert.alert(
      "Your latest changes aren't saved",
      `We couldn't save your latest changes to ${what}. Keep them on this device so they are here when you sign back in, or discard them? On a shared device, discard.`,
      [
        { text: 'Discard', style: 'destructive', onPress: () => resolve('discard') },
        { text: 'Keep on this device', style: 'cancel', onPress: () => resolve('keep') },
      ],
      { cancelable: false },
    );
  });

export interface DraftSettlement {
  saved: number;
  kept: number;
  discarded: number;
}

/**
 * Called by the sign-out wipe while the session is still valid.
 * `sessionExpired`: nobody chose to sign out and nothing can be saved, so
 * the mirrors are left as they are (bound to their owner).
 */
export async function settleLetterDraftsAtSignOut(
  options: { sessionExpired?: boolean; ask?: (count: number) => Promise<'keep' | 'discard'> } = {},
): Promise<DraftSettlement> {
  const result: DraftSettlement = { saved: 0, kept: 0, discarded: 0 };
  if (options.sessionExpired) return result;

  const owner = await currentUserId();
  let keys: string[] = [];
  try {
    keys = (await AsyncStorage.getAllKeys()).filter((k) => k.startsWith(LETTER_DRAFT_PREFIX));
  } catch {
    return result;
  }
  if (keys.length === 0) return result;

  // Loaded here, not at the top: the letter service imports the HTTP
  // client, which imports the sign-out wipe that calls this.
  let update: ((id: string, data: { letterContent: string }) => Promise<unknown>) | null = null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { letterToLovedOnesService } = require('./letterToLovedOnesInfo');
    update = (id, data) => letterToLovedOnesService.updateLetterToLovedOnes(id, data);
  } catch (error) {
    logger.warn('Letter service unavailable at sign-out', error);
  }

  const unsaved: Array<{ key: string; letterId: string; rev: number; text: string }> = [];
  const deadline = Date.now() + BUDGET_MS;
  for (const key of keys) {
    const letterId = key.slice(LETTER_DRAFT_PREFIX.length);
    let parsed: ReturnType<typeof parse>;
    try {
      parsed = parse(await AsyncStorage.getItem(key));
    } catch {
      continue;
    }
    const { draft, legacyText } = parsed;
    if (!draft && legacyText === null) continue;
    // Someone else's kept draft: not ours to send, read or remove.
    if (draft && (!owner || draft.owner !== owner)) continue;

    const text = draft ? draft.text : (legacyText as string);
    const rev = draft ? draft.rev : 0;
    let acknowledged = false;
    if (update && Date.now() < deadline) {
      const send = update;
      try {
        const outcome = await Promise.race([
          saveLetterInOrder(letterId, rev, () => send(letterId, { letterContent: text })),
          timeout(deadline - Date.now()),
        ]);
        // Not sent means newer text exists and has its own save or mirror.
        acknowledged = outcome.sent;
        if (!outcome.sent) continue;
      } catch (error) {
        logger.debug('Unsent letter draft could not be saved at sign-out', error);
      }
    }
    if (acknowledged) {
      // Only this revision: text typed while the save was in flight stays.
      await clearLetterDraftIfUnchanged(letterId, rev);
      result.saved += 1;
    } else if (draft) {
      unsaved.push({ key, letterId, rev, text });
    }
    // A legacy mirror (bare text, written before drafts carried an owner)
    // that could not be saved is left exactly as it is. Nothing here
    // shows whose it is: a refused save does not make it this account's,
    // so it is neither claimed, counted nor removed. Its author gets it
    // back when the server returns that letter to them.
  }

  if (unsaved.length === 0) return result;

  const choice = await (options.ask ?? askKeepOrDiscard)(unsaved.length);
  for (const item of unsaved) {
    if (choice === 'discard') {
      await clearLetterDraftIfUnchanged(item.letterId, item.rev);
      result.discarded += 1;
    } else {
      // Kept as it is: already bound to its owner.
      result.kept += 1;
    }
  }
  return result;
}

/** Tests only. */
export function __resetLetterDraftStateForTests(): void {
  saveChains.clear();
  acknowledgedRev.clear();
  latestRev.clear();
  lastRev = 0;
}
